(() => {
  let abortController = null;
  const DEFAULT_LANGUAGE_FLAG_ISO = "us";
  const LANGUAGE_FLAG_OVERRIDES = Object.freeze({
    en: "us",
    ja: "jp",
    nb: "no",
    nn: "no",
    sv: "se",
    ar: "sa",
    cs: "cz",
    da: "dk",
    el: "gr",
    uk: "ua",
    vi: "vn",
    zh: "cn",
    ko: "kr",
    he: "il",
    hi: "in"
  });
  const DIRECT_LANGUAGE_FLAG_ISOS = new Set([
    "de",
    "es",
    "fi",
    "fr",
    "hr",
    "hu",
    "id",
    "it",
    "lt",
    "lv",
    "nl",
    "no",
    "pl",
    "pt",
    "ro",
    "sk",
    "sl",
    "th",
    "tr"
  ]);

  const getLanguageFlagIso = (languageIsoCode) => {
    const normalizedIso = String(languageIsoCode || "")
      .trim()
      .toLowerCase()
      .replace(/_/g, "-");
    if (!normalizedIso) {
      return DEFAULT_LANGUAGE_FLAG_ISO;
    }

    const parts = normalizedIso.split("-");
    const baseIso = parts[0];
    const regionIso = parts[parts.length - 1];

    if (parts.length > 1 && regionIso.length === 2) {
      return regionIso;
    }

    if (LANGUAGE_FLAG_OVERRIDES[baseIso]) {
      return LANGUAGE_FLAG_OVERRIDES[baseIso];
    }

    if (DIRECT_LANGUAGE_FLAG_ISOS.has(baseIso)) {
      return baseIso;
    }

    return DEFAULT_LANGUAGE_FLAG_ISO;
  };

  const buildLanguageFlagUrl = (languageIsoCode) => {
    return `https://cdn.shopify.com/static/images/flags/${getLanguageFlagIso(languageIsoCode)}.svg`;
  };

  const getItemFlagUrl = (item) => {
    if (item?.flagUrl) {
      return item.flagUrl;
    }

    if (item?.currencyIsoCode) {
      return "";
    }

    return buildLanguageFlagUrl(item?.isoCode);
  };

  const setControlLabel = (control, label) => {
    if (!control) {
      return;
    }
    if (control instanceof HTMLInputElement) {
      control.value = label;
      return;
    }
    control.textContent = label;
  };

  const syncHeaderTriggers = (modal) => {
    document.querySelectorAll("[data-localization-flag]").forEach((trigger) => {
      trigger.hidden = !modal;
      if (!modal) {
        return;
      }

      const label = modal.getAttribute("data-trigger-label");
      if (label) {
        trigger.setAttribute("aria-label", label);
      }

      const flagUrl = modal.getAttribute("data-trigger-flag-url");
      const icon = trigger.querySelector("[data-localization-trigger-icon]");
      const flagImg = trigger.querySelector(".boa-localization-flag");

      if (icon) {
        icon.hidden = !flagUrl;
      }
      if (flagImg && flagUrl) {
        flagImg.src = flagUrl;
      }
    });
  };

  const initLocalizationModal = () => {
    const modal = document.getElementById("localization-modal");
    const dataScript = document.getElementById("localization-modal-data");
    const form = document.getElementById("localization-modal-form");

    if (abortController) {
      abortController.abort();
    }

    syncHeaderTriggers(modal);

    if (!modal || !dataScript || !form) {
      return;
    }

    abortController = new AbortController();
    const { signal } = abortController;

    let parsedData;
    try {
      parsedData = JSON.parse(dataScript.textContent || "{}");
    } catch {
      return;
    }

    const countries = Array.isArray(parsedData.countries) ? parsedData.countries : [];
    const languages = Array.isArray(parsedData.languages) ? parsedData.languages : [];
    const noResultsLabel = modal.getAttribute("data-no-results") || "No results";
    const languageUpdatedPrefix =
      modal.getAttribute("data-language-updated") || "Language updated to";
    const countryUpdatedPrefix =
      modal.getAttribute("data-country-updated") || "Country updated to";

    const countryHiddenInput = form.querySelector('input[name="country_code"]');
    const languageHiddenInput = form.querySelector('input[name="locale_code"]');
    const countryInput = document.getElementById("localization-modal-country-input");
    const countrySearchInput = form.querySelector(
      '[data-list="country"] .localization-modal__search-input'
    );
    const languageInput = document.getElementById("localization-modal-language-input");
    const countryFlag = modal.querySelector(
      ".localization-modal__country-input-wrap .boa-localization-flag"
    );
    const countryList = form.querySelector('[data-list="country"]');
    const countryOptions = form.querySelector('[data-list-options="country"]');
    const languageList = form.querySelector('[data-list="language"]');
    const languageOptions = form.querySelector('[data-list-options="language"]');
    const countryField = countryInput?.closest(".localization-modal__field");
    const languageField = languageInput?.closest(".localization-modal__field");
    const liveRegion = document.getElementById("localization-modal-live");
    const syncNote = document.getElementById("localization-modal-sync-note");

    if (!countryHiddenInput || !languageHiddenInput) {
      return;
    }

    const currentCountryIsoCode = modal.getAttribute("data-current-country") || "";
    const currentLanguageIsoCode = modal.getAttribute("data-current-language") || "";

    // Don't let opening localization suppress the newsletter popup "show once" flag.
    modal.addEventListener(
      "openable-element:open",
      () => {
        let hadAppeared = false;
        try {
          hadAppeared = localStorage.getItem("theme:popup-appeared") !== null;
        } catch {
          return;
        }
        queueMicrotask(() => {
          if (hadAppeared) {
            return;
          }
          try {
            localStorage.removeItem("theme:popup-appeared");
          } catch {
            // private/incognito storage can throw
          }
        });
      },
      { signal }
    );

    const formatCountryLabel = (country) => {
      const symbol = country.currencySymbol ? ` ${country.currencySymbol}` : "";
      return `${country.name} (${country.currencyIsoCode}${symbol})`;
    };

    const updateCountryFlag = (country) => {
      if (!countryFlag) {
        return;
      }
      if (country?.flagUrl) {
        countryFlag.src = country.flagUrl;
        countryFlag.hidden = false;
      } else {
        countryFlag.removeAttribute("src");
        countryFlag.hidden = true;
      }
    };

    const languagesForCountry = (countryIsoCode) => {
      const country = countries.find((item) => item.isoCode === countryIsoCode);
      if (Array.isArray(country?.languages) && country.languages.length > 0) {
        return country.languages;
      }
      return languages;
    };

    const findLanguage = (isoCode, countryIsoCode) => {
      return (
        languagesForCountry(countryIsoCode || countryHiddenInput.value || currentCountryIsoCode).find(
          (language) => language.isoCode === isoCode
        ) || languages.find((language) => language.isoCode === isoCode)
      );
    };

    const flashSyncedField = (field) => {
      if (!field) {
        return;
      }
      field.classList.add("localization-modal__field--synced");
      window.setTimeout(() => {
        field.classList.remove("localization-modal__field--synced");
      }, 1600);
    };

    const clearSyncNote = () => {
      if (!syncNote) {
        return;
      }
      syncNote.textContent = "";
      syncNote.hidden = true;
    };

    const announceLanguageUpdate = (languageName) => {
      if (liveRegion) {
        liveRegion.textContent = `${languageUpdatedPrefix} ${languageName}`;
      }
      flashSyncedField(languageField);
    };

    const announceCountryUpdate = (country) => {
      const message = `${countryUpdatedPrefix} ${formatCountryLabel(country)}`;
      if (liveRegion) {
        liveRegion.textContent = message;
      }
      if (syncNote) {
        syncNote.textContent = message;
        syncNote.hidden = false;
      }
      flashSyncedField(countryField || languageField);
    };

    const syncLanguageToCountry = (countryIsoCode, preferredLanguageIsoCode, { announce = false } = {}) => {
      const available = languagesForCountry(countryIsoCode);
      if (!available.length) {
        return;
      }

      const previousIsoCode = languageHiddenInput.value;
      const preferred =
        available.find((language) => language.isoCode === preferredLanguageIsoCode) ||
        available.find((language) => language.isoCode === currentLanguageIsoCode) ||
        available[0];

      languageHiddenInput.value = preferred.isoCode;
      setControlLabel(languageInput, preferred.name);

      if (announce && preferred.isoCode !== previousIsoCode) {
        announceLanguageUpdate(preferred.name);
      }
    };

    // Shopify rejects unpublished locale+country pairs — especially on a fresh/incognito
    // session with no localization cookie. When the shopper picks a language, move
    // country_code to a market that actually offers it before submit.
    const ensureCountryForLanguage = (languageIsoCode, { announce = false } = {}) => {
      if (!languageIsoCode) {
        return;
      }

      const currentIsoCode = countryHiddenInput.value || currentCountryIsoCode;
      const currentCountry = countries.find((country) => country.isoCode === currentIsoCode);
      const currentLanguages = Array.isArray(currentCountry?.languages) ? currentCountry.languages : null;

      if (
        Array.isArray(currentLanguages) &&
        currentLanguages.length > 0 &&
        currentLanguages.some((language) => language.isoCode === languageIsoCode)
      ) {
        return;
      }

      const match = countries.find(
        (country) =>
          Array.isArray(country.languages) &&
          country.languages.some((language) => language.isoCode === languageIsoCode)
      );
      if (!match || match.isoCode === currentIsoCode) {
        return;
      }

      countryHiddenInput.value = match.isoCode;
      setControlLabel(countryInput, formatCountryLabel(match));
      updateCountryFlag(match);
      if (announce) {
        announceCountryUpdate(match);
      }
    };

    let isSubmitting = false;

    form.addEventListener(
      "submit",
      () => {
        isSubmitting = true;
        ensureCountryForLanguage(languageHiddenInput.value);
      },
      { signal }
    );

    const setExpanded = (input, searchInput, expanded) => {
      const value = expanded ? "true" : "false";
      if (input) {
        input.setAttribute("aria-expanded", value);
      }
      if (searchInput) {
        searchInput.setAttribute("aria-expanded", value);
      }
    };

    const setFieldOpen = (field, open) => {
      if (!field) {
        return;
      }
      field.classList.toggle("localization-modal__field--open", open);
    };

    const createCombobox = ({
      input,
      listElement,
      optionsElement,
      searchInput = null,
      field,
      getItems,
      buildLabel,
      getSelectedValue,
      onSelect,
      includeFlag = false,
      idPrefix,
      onBeforeOpen = () => {}
    }) => {
      if (!input || !listElement || !optionsElement) {
        return null;
      }

      let activeIndex = -1;
      let visibleItems = [];
      const activeDescendantTarget = searchInput || input;

      const isOpen = () => !listElement.hidden;

      const clearActive = () => {
        activeIndex = -1;
        activeDescendantTarget.removeAttribute("aria-activedescendant");
        optionsElement.querySelectorAll(".localization-modal__option--active").forEach((option) => {
          option.classList.remove("localization-modal__option--active");
        });
      };

      const setActive = (index) => {
        const options = [...optionsElement.querySelectorAll('[role="option"]:not([aria-disabled="true"])')];
        if (!options.length) {
          clearActive();
          return;
        }

        activeIndex = ((index % options.length) + options.length) % options.length;
        options.forEach((option, optionIndex) => {
          option.classList.toggle("localization-modal__option--active", optionIndex === activeIndex);
        });

        const activeOption = options[activeIndex];
        activeDescendantTarget.setAttribute("aria-activedescendant", activeOption.id);
        activeOption.scrollIntoView({ block: "nearest" });
      };

      const close = () => {
        listElement.hidden = true;
        listElement.style.maxHeight = "";
        if (searchInput) {
          searchInput.value = "";
        }
        optionsElement.innerHTML = "";
        visibleItems = [];
        clearActive();
        setExpanded(input, searchInput, false);
        setFieldOpen(field, false);
      };

      const constrainListHeight = () => {
        if (listElement.hidden) {
          return;
        }

        const viewport = window.visualViewport;
        const viewportHeight = viewport?.height ?? window.innerHeight;
        const viewportTop = viewport?.offsetTop ?? 0;
        const listTop = listElement.getBoundingClientRect().top;
        const available = viewportTop + viewportHeight - listTop - 12;
        const maxHeight = Math.max(120, Math.min(available, 256));

        listElement.style.maxHeight = `${maxHeight}px`;
      };

      const selectValue = (isoCode, label, item) => {
        onSelect(isoCode, label, item);
        close();
        input.focus();
      };

      const render = (query = "") => {
        const items = getItems();
        const normalizedQuery = String(query || "").trim().toLowerCase();
        visibleItems = normalizedQuery
          ? items.filter((item) => buildLabel(item).toLowerCase().includes(normalizedQuery))
          : items;

        optionsElement.innerHTML = "";
        clearActive();

        if (visibleItems.length === 0) {
          const empty = document.createElement("div");
          empty.className = "localization-modal__option localization-modal__option--empty";
          empty.id = `${idPrefix}-empty`;
          empty.setAttribute("role", "option");
          empty.setAttribute("aria-disabled", "true");
          empty.textContent = noResultsLabel;
          optionsElement.appendChild(empty);
        } else {
          const selectedValue = getSelectedValue();
          visibleItems.forEach((item, index) => {
            const option = document.createElement("div");
            const label = buildLabel(item);
            option.className = "localization-modal__option";
            option.id = `${idPrefix}-${item.isoCode}-${index}`;
            option.setAttribute("role", "option");
            option.setAttribute("aria-selected", item.isoCode === selectedValue ? "true" : "false");
            if (item.isoCode === selectedValue) {
              option.dataset.selected = "true";
            }
            option.dataset.value = item.isoCode;
            option.tabIndex = -1;

            if (includeFlag) {
              const flagBox = document.createElement("span");
              flagBox.className = "boa-localization-flag-box";
              flagBox.setAttribute("aria-hidden", "true");

              const flagUrl = getItemFlagUrl(item);
              if (flagUrl) {
                const flag = document.createElement("img");
                flag.className = "boa-localization-flag";
                flag.src = flagUrl;
                flag.alt = "";
                flag.width = 20;
                flag.height = 15;
                flag.loading = "lazy";
                flagBox.appendChild(flag);
              }

              const labelSpan = document.createElement("span");
              labelSpan.className = "localization-modal__option-label";
              labelSpan.textContent = label;
              option.append(flagBox, labelSpan);
            } else {
              option.textContent = label;
            }

            optionsElement.appendChild(option);
          });
        }

        listElement.hidden = false;
        setExpanded(input, searchInput, true);
        setFieldOpen(field, true);
        constrainListHeight();
      };

      const open = ({ focusSearch = false } = {}) => {
        onBeforeOpen();
        render(searchInput?.value || "");
        requestAnimationFrame(() => {
          constrainListHeight();
          window.setTimeout(constrainListHeight, 300);
        });
        if (focusSearch && searchInput) {
          searchInput.focus();
        }

        const selectedValue = getSelectedValue();
        const selectedIndex = visibleItems.findIndex((item) => item.isoCode === selectedValue);
        if (selectedIndex >= 0) {
          setActive(selectedIndex);
        }
      };

      optionsElement.addEventListener(
        "click",
        (event) => {
          const option = event.target.closest("[data-value]");
          if (!(option instanceof HTMLElement) || option.getAttribute("aria-disabled") === "true") {
            return;
          }
          const item = visibleItems.find((entry) => entry.isoCode === option.dataset.value);
          if (!item) {
            return;
          }
          selectValue(item.isoCode, buildLabel(item), item);
        },
        { signal }
      );

      const onKeyDown = (event) => {
        const key = event.key;
        const openState = isOpen();

        if (key === "Enter" || key === " ") {
          if (key === " " && event.currentTarget !== input) {
            return;
          }
          event.preventDefault();
          if (!openState) {
            open({ focusSearch: Boolean(searchInput) && event.currentTarget === input });
            return;
          }
          if (activeIndex >= 0 && visibleItems[activeIndex]) {
            const item = visibleItems[activeIndex];
            selectValue(item.isoCode, buildLabel(item), item);
          }
          return;
        }

        if (key === "ArrowDown") {
          event.preventDefault();
          if (!openState) {
            open();
            return;
          }
          setActive(activeIndex + 1);
          return;
        }

        if (key === "ArrowUp") {
          event.preventDefault();
          if (!openState) {
            open();
            return;
          }
          setActive(activeIndex <= 0 ? visibleItems.length - 1 : activeIndex - 1);
          return;
        }

        if (key === "Home" && openState) {
          event.preventDefault();
          setActive(0);
          return;
        }

        if (key === "End" && openState) {
          event.preventDefault();
          setActive(visibleItems.length - 1);
        }
      };

      input.addEventListener(
        "click",
        () => {
          if (isOpen()) {
            close();
            return;
          }
          open({ focusSearch: Boolean(searchInput) });
        },
        { signal }
      );
      input.addEventListener("keydown", onKeyDown, { signal });

      searchInput?.addEventListener(
        "input",
        () => {
          render(searchInput.value);
          if (visibleItems.length) {
            setActive(0);
          }
        },
        { signal }
      );
      searchInput?.addEventListener("keydown", onKeyDown, { signal });

      if (field) {
        field.addEventListener(
          "focusout",
          (event) => {
            if (!field.contains(event.relatedTarget)) {
              close();
            }
          },
          { signal }
        );
      }

      return {
        close,
        isOpen,
        focus: () => input.focus(),
        listElement,
        input,
        constrainListHeight
      };
    };

    let countryCombobox = null;
    let languageCombobox = null;

    const constrainOpenListHeights = () => {
      countryCombobox?.constrainListHeight?.();
      languageCombobox?.constrainListHeight?.();
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", constrainOpenListHeights, { signal });
      window.visualViewport.addEventListener("scroll", constrainOpenListHeights, { signal });
    }

    const closeLists = () => {
      countryCombobox?.close();
      languageCombobox?.close();
    };

    syncLanguageToCountry(countryHiddenInput.value || currentCountryIsoCode, languageHiddenInput.value);
    const baselineCountryIsoCode = countryHiddenInput.value;
    const baselineLanguageIsoCode = languageHiddenInput.value;

    const resetToCurrent = () => {
      const baselineCountry = countries.find((country) => country.isoCode === baselineCountryIsoCode);
      const baselineLanguage = findLanguage(baselineLanguageIsoCode, baselineCountryIsoCode);

      countryHiddenInput.value = baselineCountryIsoCode;
      languageHiddenInput.value = baselineLanguageIsoCode;

      if (countryInput && baselineCountry) {
        setControlLabel(countryInput, formatCountryLabel(baselineCountry));
        updateCountryFlag(baselineCountry);
      }

      if (languageInput && baselineLanguage) {
        setControlLabel(languageInput, baselineLanguage.name);
      }

      clearSyncNote();
      closeLists();
    };

    if (countryInput && countryList && countryOptions) {
      countryCombobox = createCombobox({
        input: countryInput,
        listElement: countryList,
        optionsElement: countryOptions,
        searchInput: countrySearchInput,
        field: countryField,
        getItems: () => countries,
        buildLabel: formatCountryLabel,
        getSelectedValue: () => countryHiddenInput.value,
        includeFlag: true,
        idPrefix: "localization-country-option",
        onBeforeOpen: () => languageCombobox?.close(),
        onSelect: (isoCode, label, item) => {
          countryHiddenInput.value = isoCode;
          setControlLabel(countryInput, label);
          updateCountryFlag(item);
          clearSyncNote();
          syncLanguageToCountry(isoCode, languageHiddenInput.value, { announce: true });
        }
      });
    }

    if (languageInput && languageList && languageOptions) {
      languageCombobox = createCombobox({
        input: languageInput,
        listElement: languageList,
        optionsElement: languageOptions,
        field: languageField,
        // Always list every published locale; ensureCountryForLanguage relocates the market.
        getItems: () => languages,
        buildLabel: (language) => language.name,
        getSelectedValue: () => languageHiddenInput.value,
        idPrefix: "localization-language-option",
        includeFlag: true,
        onBeforeOpen: () => countryCombobox?.close(),
        onSelect: (isoCode, label) => {
          languageHiddenInput.value = isoCode;
          setControlLabel(languageInput, label);
          ensureCountryForLanguage(isoCode, { announce: true });
        }
      });
    }

    const eventIsInsideCombobox = (combobox, target) => {
      if (!combobox) {
        return false;
      }
      return (
        combobox.listElement.contains(target) ||
        combobox.input === target ||
        combobox.input.contains(target)
      );
    };

    const closeListsOutsideTarget = (target) => {
      if (!(target instanceof Element)) {
        return false;
      }

      let closed = false;
      if (countryCombobox?.isOpen() && !eventIsInsideCombobox(countryCombobox, target)) {
        countryCombobox.close();
        closed = true;
      }
      if (languageCombobox?.isOpen() && !eventIsInsideCombobox(languageCombobox, target)) {
        languageCombobox.close();
        closed = true;
      }
      return closed;
    };

    const overlay = modal.querySelector(".modal__overlay");
    let suppressOverlayClose = false;

    document.addEventListener(
      "pointerdown",
      (event) => {
        const onOverlay = Boolean(overlay && event.target === overlay);
        const closedAList = closeListsOutsideTarget(event.target);
        // pointerdown closes the list before click, so remember that this overlay
        // gesture should not also dismiss the modal (two-step close).
        suppressOverlayClose = onOverlay && closedAList;
      },
      { capture: true, signal }
    );

    overlay?.addEventListener(
      "click",
      (event) => {
        if (!suppressOverlayClose) {
          return;
        }
        suppressOverlayClose = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { capture: true, signal }
    );

    // Beat Focal's capture-phase Escape so open lists close without destroying the modal.
    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Escape") {
          return;
        }

        const countryOpen = Boolean(countryCombobox?.isOpen());
        const languageOpen = Boolean(languageCombobox?.isOpen());
        if (!countryOpen && !languageOpen) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        closeLists();
        if (countryOpen) {
          countryCombobox.focus();
        } else if (languageOpen) {
          languageCombobox.focus();
        }
      },
      { capture: true, signal }
    );

    modal.addEventListener(
      "openable-element:close",
      () => {
        if (isSubmitting) {
          return;
        }
        resetToCurrent();
      },
      { signal }
    );
  };

  initLocalizationModal();

  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    if (!(section instanceof Element)) {
      return;
    }

    const modal = document.getElementById("localization-modal");
    if (section.matches(".shopify-section--boa-localization-modal")) {
      initLocalizationModal();
      return;
    }

    // Header (or other) reloads remount the trigger; re-apply visibility/presentation.
    syncHeaderTriggers(modal);
  });
})();
